/**
 * Widerruf form (§ 356a BGB, ab 19.06.2026) with Klaviyo event submission.
 *
 * The form is intentionally minimal — the law requires only three fields
 * (Name, Bestellnummer, E-Mail); the reason is optional. The "Widerruf
 * bestätigen" button is the action that actually triggers the withdrawal.
 *
 * On confirm the form fires a Klaviyo client *event* (metric) carrying every
 * field plus the receipt date/time as event properties, attributed to the
 * customer's profile. In Klaviyo that metric triggers the confirmation email
 * to the customer (content + date + time, with a link to the returns portal).
 *
 * Uses only the public Company ID — safe to expose in the browser.
 * Docs: https://developers.klaviyo.com/en/reference/create_client_event
 */
document.addEventListener('DOMContentLoaded', () => {
  const forms = document.querySelectorAll('.js-widerruf-form');

  forms.forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      await handleSubmit(form);
    });

    // Clear a field's inline error as soon as the user starts correcting it.
    form.querySelectorAll('[name]').forEach(input => {
      input.addEventListener('input', () => clearFieldError(form, input.name));
    });
  });

  function showError(form, message) {
    const errorMsg = form.querySelector('.js-error-message');
    const errorText = form.querySelector('.js-error-text');
    if (errorMsg) {
      if (errorText && message) errorText.textContent = message;
      errorMsg.classList.remove('hidden');
    }
  }

  function hideError(form) {
    form.querySelector('.js-error-message')?.classList.add('hidden');
  }

  /** Show an inline error directly beneath the matching input. */
  function setFieldError(form, name, message) {
    const errorEl = form.querySelector(`.js-field-error[data-error-for="${name}"]`);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    }
    const input = form.querySelector(`[name="${name}"]`);
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      // border-erdbeere isn't in the precompiled Tailwind output, so drive the
      // red border via the existing CSS variable instead of a utility class.
      const box = input.closest('.js-field-box');
      if (box) box.style.borderColor = 'var(--color-erdbeere)';
    }
  }

  /** Reset the inline error state for a single field. */
  function clearFieldError(form, name) {
    const errorEl = form.querySelector(`.js-field-error[data-error-for="${name}"]`);
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }
    const input = form.querySelector(`[name="${name}"]`);
    if (input) {
      input.setAttribute('aria-invalid', 'false');
      // Clear the inline override so the default border-aubergine/30 returns.
      const box = input.closest('.js-field-box');
      if (box) box.style.borderColor = '';
    }
  }

  function clearFieldErrors(form) {
    form
      .querySelectorAll('.js-field-error[data-error-for]')
      .forEach(el => clearFieldError(form, el.getAttribute('data-error-for')));
  }

  /** Hide the form body and reveal the confirmation panel (with returns CTA). */
  function showSuccess(form) {
    hideError(form);
    form.querySelector('.js-widerruf-body')?.classList.add('hidden');
    const success = form.querySelector('.js-success-message');
    if (success) {
      success.classList.remove('hidden');
      success.classList.add('flex');
    }
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Local receipt timestamp in the customer's language, e.g.
   * "9. Juni 2026 um 07:31" (de) / "9 juin 2026 à 07:31" (fr).
   * The timezone stays Europe/Berlin — WECK versendet aus Deutschland, the
   * legally relevant receipt time is the German one; only the wording follows
   * the storefront locale.
   */
  function receiptTimestamp(locale) {
    const now = new Date();
    try {
      return now.toLocaleString(locale || 'de', {
        dateStyle: 'long',
        timeStyle: 'short',
        timeZone: 'Europe/Berlin',
      });
    } catch (e) {
      return now.toLocaleString('de-DE');
    }
  }

  /** Create a Klaviyo client event for the given profile + properties. */
  async function trackEvent(companyId, eventName, profile, properties) {
    const response = await fetch(
      `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties,
              metric: {
                data: { type: 'metric', attributes: { name: eventName } },
              },
              profile: {
                data: { type: 'profile', attributes: profile },
              },
            },
          },
        }),
      }
    );

    // Success: 202 Accepted with an empty body — do NOT try to parse JSON.
    if (response.status === 202) return;

    const errorData = await response.json().catch(() => ({}));
    const detail =
      errorData.errors && errorData.errors[0] && errorData.errors[0].detail;
    throw new Error(detail || `HTTP error! status: ${response.status}`);
  }

  async function handleSubmit(form) {
    hideError(form);
    clearFieldErrors(form);

    // Honeypot — silently accept bot submissions without calling Klaviyo.
    if (form.querySelector('[name="company_website"]')?.value) {
      showSuccess(form);
      return;
    }

    const companyId = form.getAttribute('data-klaviyo-company-id');
    const eventName =
      form.getAttribute('data-klaviyo-event-name') || 'Widerruf eingereicht';
    const submitButton = form.querySelector('button[type="submit"]');

    // Localized messages come from data-* attributes (rendered via the theme's
    // `t` filter). The German strings are last-resort fallbacks only.
    const msgs = {
      nameRequired:
        form.dataset.msgNameRequired || 'Bitte gib deinen Namen ein.',
      orderRequired:
        form.dataset.msgOrderRequired || 'Bitte gib deine Bestellnummer ein.',
      emailInvalid:
        form.dataset.msgEmailInvalid ||
        'Bitte gib eine gültige E-Mail-Adresse ein.',
      configMissing:
        form.dataset.msgConfigMissing ||
        'Klaviyo-Konfiguration fehlt. Bitte kontaktiere den Administrator.',
      generic:
        form.dataset.msgGeneric ||
        'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.',
      invalidCompanyId:
        form.dataset.msgInvalidCompanyId ||
        'Ungültige Klaviyo Company ID. Bitte kontaktiere den Administrator.',
    };

    const fd = new FormData(form);
    const name = (fd.get('name') || '').toString().trim();
    const email = (fd.get('email') || '').toString().trim();
    const orderNumber = (fd.get('order_number') || '').toString().trim();
    const reason = (fd.get('reason') || '').toString().trim();

    // Field-level validation shows inline errors; collect all of them at once
    // and focus the first invalid field.
    let firstInvalid = null;
    if (!name) {
      setFieldError(form, 'name', msgs.nameRequired);
      firstInvalid = firstInvalid || form.querySelector('[name="name"]');
    }
    if (!orderNumber) {
      setFieldError(form, 'order_number', msgs.orderRequired);
      firstInvalid = firstInvalid || form.querySelector('[name="order_number"]');
    }
    if (!isValidEmail(email)) {
      setFieldError(form, 'email', msgs.emailInvalid);
      firstInvalid = firstInvalid || form.querySelector('input[type="email"]');
    }
    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }
    if (!companyId) {
      showError(form, msgs.configMissing);
      return;
    }

    // Storefront locale (de/fr/it/nl/en) — drives the wording of the localized
    // receipt timestamp below and the profile locale handed to Klaviyo.
    const language = (form.dataset.locale || 'de').toLowerCase();
    const receivedAt = receiptTimestamp(language);

    // Klaviyo picks the translation of the flow email from the *profile's*
    // `locale` (Account > Sprachen > "Wie man die bevorzugte Sprache
    // bestimmt: Locale") — event properties are invisible to it. Write the
    // full {language}-{country} tag, matching the shape Shopify already syncs
    // (de-DE, fr-DE, de-NL …); Klaviyo maps every de-* variant to Deutsch and
    // every fr-* variant to Franzoesisch.
    const country = (form.dataset.country || '').toLowerCase();
    const profileLocale = country ? language + '-' + country : language;

    // email/first_name are profile attributes Klaviyo understands natively;
    // the properties carry the full withdrawal content for the flow templates.
    const profile = { email, first_name: name, locale: profileLocale };
    const properties = {
      name,
      order_number: orderNumber,
      email,
      received_at: receivedAt,
      received_at_iso: new Date().toISOString(),
    };
    if (reason) properties.reason = reason;

    submitButton.disabled = true;
    submitButton.classList.add('opacity-50', 'cursor-not-allowed');

    try {
      await trackEvent(companyId, eventName, profile, properties);
      showSuccess(form);
    } catch (error) {
      console.error('[widerruf-form] Klaviyo event failed:', error);
      let message = msgs.generic;
      if (error.message.includes('401') || error.message.includes('403')) {
        message = msgs.invalidCompanyId;
      }
      showError(form, message);
    } finally {
      submitButton.disabled = false;
      submitButton.classList.remove('opacity-50', 'cursor-not-allowed');
    }
  }
});
